/** SUNTRONIX ONE — Admin Mode context.
 *
 *  Session-scoped, super_admin-only.  Defaults OFF; never enabled
 *  automatically.  Reset to OFF on logout via the storage-scoped key.
 *  Renders a persistent subtle chip in the SpacesShell header when ON.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Settings, X, ArrowRight } from "lucide-react";

const AdminModeCtx = createContext({ on: false, canAdmin: false, toggle: () => {} });
const STORAGE_KEY = "st.adminMode.on";

export function AdminModeProvider({ user, children }) {
  const canAdmin = user?.role === "super_admin";
  const [on, setOn] = useState(() => {
    try { return canAdmin && sessionStorage.getItem(STORAGE_KEY) === "1"; }
    catch { return false; }
  });

  // Reset OFF and clear storage whenever the signed-in user changes
  // (logout → login of the same admin should start with Admin Mode OFF).
  useEffect(() => {
    if (!user) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      if (on) setOn(false);
      return;
    }
    if (!canAdmin && on) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      setOn(false);
    }
  }, [user, canAdmin, on]);

  const toggle = useCallback(() => {
    if (!canAdmin) return;
    setOn((prev) => {
      const next = !prev;
      try { sessionStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  }, [canAdmin]);

  return (
    <AdminModeCtx.Provider value={{ on, canAdmin, toggle }}>
      {children}
    </AdminModeCtx.Provider>
  );
}

export function useAdminMode() { return useContext(AdminModeCtx); }

/** Global toggle chip rendered in the SpacesShell top bar. */
export function AdminModeToggle() {
  const { on, canAdmin, toggle } = useAdminMode();
  if (!canAdmin) return null;
  return (
    <button
      onClick={toggle}
      data-testid="one-bar-admin-mode"
      title={on ? "Admin Mode ON — click to turn off" : "Enable Admin Mode"}
      aria-pressed={on}
      className={"h-9 px-2.5 rounded-md text-[11px] font-medium inline-flex items-center gap-1.5 transition-colors " +
                              (on
                                  ? "bg-amber-100 text-amber-900 ring-1 ring-amber-300 hover:bg-amber-200"
                                  : "text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]")}>
      <Wrench className="h-3.5 w-3.5" strokeWidth={1.7} />
      <span className="hidden md:inline">
        {on ? "Admin Mode" : "Admin"}
      </span>
      {on && <span className="hidden lg:inline text-[9px] uppercase tracking-wider">ON</span>}
    </button>
  );
}

/** Contextual "configure" chip that pages sprinkle where relevant.
 *
 *    <AdminChip area="quotation" label="Configure Quote Form" />
 *    <AdminChip area="picklists" pl="industry" label="Edit options" />
 *    <AdminChip area="modules" label="Edit navigation" />
 *
 *  Only renders when Admin Mode is ON.  Clicking either opens a
 *  compact inline drawer (label + preview) or deep-links to the
 *  matching Configuration Studio tab — never a duplicate settings
 *  page.
 */
export function AdminChip({
    area, pl, label = "Configure",
    to,
    testid, className = "" }) {
  const { on } = useAdminMode();
  const navigate = useNavigate();
  const [openHint, setOpenHint] = useState(false);

  if (!on) return null;

  const target = to || (() => {
    if (area === "picklists" && pl)
      return `/administration/config-studio?tab=picklists&pl=${encodeURIComponent(pl)}`;
    if (area === "modules")
      return `/administration/config-studio?tab=modules`;
    if (area === "history")
      return `/administration/config-studio?tab=history`;
    return `/administration/config-studio?tab=${encodeURIComponent(area || "quotation")}`;
  })();

  return (
    <span className="relative inline-flex" data-testid={testid || `admin-chip-${area || "generic"}`}>
      <button
          onClick={() => setOpenHint((v) => !v)}
          onDoubleClick={() => navigate(target)}
          className={"text-[10px] px-1.5 py-0.5 rounded-md ring-1 " +
                                  "bg-amber-50 text-amber-800 ring-amber-200 hover:bg-amber-100 " +
                                  "inline-flex items-center gap-1 " + className}>
        <Settings className="h-2.5 w-2.5" strokeWidth={1.8} />
        {label}
      </button>
      {openHint && (
        <div className="absolute z-40 top-full right-0 mt-1 w-64 bg-white border border-amber-200 rounded-md shadow-lg p-2 text-[11px]"
             onMouseLeave={() => setOpenHint(false)}>
          <div className="text-slate-500 mb-2">
            This action opens the governed Configuration Studio for {area || "this area"}.
            History and Restore are preserved automatically.
          </div>
          <button onClick={() => { setOpenHint(false); navigate(target); }}
              className="w-full text-left px-2 py-1 rounded bg-slate-900 text-white text-[11px] inline-flex items-center justify-center gap-1"
              data-testid="admin-chip-open">
            Open full configuration <ArrowRight className="h-3 w-3" />
          </button>
          <button onClick={() => setOpenHint(false)}
              className="w-full text-left px-2 py-1 mt-1 rounded border border-slate-200 text-[10px] text-slate-500 inline-flex items-center justify-center gap-1">
            <X className="h-3 w-3" /> Close
          </button>
        </div>
      )}
    </span>
  );
}
